// ONE list of every screen: its URL, its page, its menu entry and the permission it needs. The routes
// (routes.tsx) and the left menu (menus.ts) are both built from this list, so a new screen is one entry here.
import { lazy, type ComponentType, type LazyExoticComponent } from 'react'
import type { LucideIcon } from 'lucide-react'
import {
  AudioLines,
  Building2,
  ClipboardCheck,
  Database,
  GitBranch,
  History,
  Home,
  Image as ImageIcon,
  Landmark,
  Layers,
  ListChecks,
  MessageCircle,
  MessageSquare,
  Megaphone,
  PhoneCall,
  Radio,
  Settings,
  ShieldOff,
  Smartphone,
  UserCog,
  Users,
  UsersRound,
} from 'lucide-react'
import { PERMISSION, type Permission } from '@/constants/permissions'
import { ROUTES } from '@/constants/routes'

export type MenuGroupName = '' | 'Campaigns' | 'Channels' | 'Data' | 'Vendors' | 'Admin'
/** a small counter next to the menu item */
export type MenuBadge = 'approvals' | 'suppression'

export interface Screen {
  path: string
  title: string
  page: LazyExoticComponent<ComponentType>
  /** who may open it; none = everyone signed in */
  need?: Permission
  menu?: { label: string; group: MenuGroupName; icon: LucideIcon; badge?: MenuBadge }
}

const page = (load: () => Promise<{ default: ComponentType }>) => lazy(load)

export const SCREENS: Screen[] = [
  {
    path: ROUTES.overview.path,
    title: 'Overview',
    page: page(() => import('@/features/overview/pages/OverviewPage')),
    menu: { label: 'Overview', group: '', icon: Home },
  },
  {
    path: ROUTES.campaigns.path,
    title: 'Campaigns',
    page: page(() => import('@/features/campaigns/pages/CampaignsPage')),
    menu: { label: 'Campaigns', group: 'Campaigns', icon: Megaphone },
  },
  // the wizard's two addresses come before /campaigns/:id, so the top bar finds them first
  {
    path: ROUTES.campaignNew.path,
    title: 'New campaign',
    page: page(() => import('@/features/campaign-wizard/pages/CampaignWizardPage')),
    need: PERMISSION.campaignsEdit,
  },
  {
    path: ROUTES.campaignEdit.path,
    title: 'Campaign draft',
    page: page(() => import('@/features/campaign-wizard/pages/CampaignWizardPage')),
    need: PERMISSION.campaignsEdit,
  },
  {
    path: ROUTES.campaign.path,
    title: 'Campaign',
    page: page(() => import('@/features/campaigns/pages/CampaignDetailPage')),
  },
  {
    path: ROUTES.audiences.path,
    title: 'Audiences',
    page: page(() => import('@/features/audiences/pages/AudiencesPage')),
    menu: { label: 'Audiences', group: 'Campaigns', icon: UsersRound },
  },
  {
    path: ROUTES.audienceNew.path,
    title: 'New audience',
    page: page(() => import('@/features/audiences/pages/AudienceBuilderPage')),
  },
  {
    path: ROUTES.audience.path,
    title: 'Audience',
    page: page(() => import('@/features/audiences/pages/AudienceDetailPage')),
  },
  {
    path: ROUTES.workflows.path,
    title: 'Workflows',
    page: page(() => import('@/features/workflows/pages/WorkflowsPage')),
    menu: { label: 'Workflows', group: 'Campaigns', icon: GitBranch },
  },
  {
    path: ROUTES.workflow.path,
    title: 'Workflow',
    // the canvas (React Flow) is heavy: this lazy page is the only one that loads it
    page: page(() => import('@/features/workflows/pages/WorkflowDetailPage')),
  },
  {
    path: ROUTES.approvals.path,
    title: 'Approvals',
    page: page(() => import('@/features/approvals/pages/ApprovalsPage')),
    need: PERMISSION.campaignsApprove,
    menu: { label: 'Approvals', group: 'Campaigns', icon: ClipboardCheck, badge: 'approvals' },
  },
  {
    path: ROUTES.whatsappNumbers.path,
    title: 'WhatsApp numbers',
    page: page(() => import('@/features/whatsapp-numbers/pages/WhatsAppNumbersPage')),
    menu: { label: 'WhatsApp numbers', group: 'Channels', icon: Smartphone },
  },
  {
    path: ROUTES.whatsappTemplates.path,
    title: 'WhatsApp templates',
    page: page(() => import('@/features/whatsapp-templates/pages/WhatsAppTemplatesPage')),
    menu: { label: 'WhatsApp templates', group: 'Channels', icon: MessageCircle },
  },
  {
    path: ROUTES.ivr.path,
    title: 'IVR numbers',
    page: page(() => import('@/features/ivr/pages/IvrPage')),
    menu: { label: 'IVR numbers', group: 'Channels', icon: PhoneCall },
  },
  {
    path: ROUTES.voiceMessages.path,
    title: 'Voice messages',
    page: page(() => import('@/features/voice-messages/pages/VoiceMessagesPage')),
    menu: { label: 'Voice messages', group: 'Channels', icon: AudioLines },
  },
  {
    path: ROUTES.sms.path,
    title: 'SMS',
    page: page(() => import('@/features/sms/pages/SmsPage')),
    menu: { label: 'SMS', group: 'Channels', icon: MessageSquare },
  },
  {
    path: ROUTES.images.path,
    title: 'Images',
    page: page(() => import('@/features/images/pages/ImagesPage')),
    menu: { label: 'Images', group: 'Channels', icon: ImageIcon },
  },
  {
    path: ROUTES.dataSources.path,
    title: 'Data sources',
    page: page(() => import('@/features/data-sources/pages/DataSourcesPage')),
    menu: { label: 'Data sources', group: 'Data', icon: Database },
  },
  {
    path: ROUTES.leads.path,
    title: 'Leads',
    page: page(() => import('@/features/leads/pages/LeadsPage')),
    menu: { label: 'Leads', group: 'Data', icon: Users },
  },
  {
    path: ROUTES.suppression.path,
    title: 'Suppression list',
    page: page(() => import('@/features/suppression/pages/SuppressionPage')),
    menu: { label: 'Suppression list', group: 'Data', icon: ShieldOff, badge: 'suppression' },
  },
  {
    path: ROUTES.lists.path,
    title: 'Lists',
    page: page(() => import('@/features/lists/pages/ListsPage')),
    menu: { label: 'Lists', group: 'Data', icon: ListChecks },
  },
  {
    path: ROUTES.batches.path,
    title: 'Batches',
    page: page(() => import('@/features/batches/pages/BatchesPage')),
    menu: { label: 'Batches', group: 'Data', icon: Layers },
  },
  {
    path: ROUTES.batch.path,
    title: 'Batch',
    page: page(() => import('@/features/batches/pages/BatchDetailPage')),
  },
  {
    path: ROUTES.vendors.path,
    title: 'Vendors',
    page: page(() => import('@/features/vendors/pages/VendorsPage')),
    menu: { label: 'Vendors', group: 'Vendors', icon: Building2 },
  },
  // the Lender Hub's screens come before /vendors/:id, so the top bar finds them first
  {
    path: ROUTES.lenderHub.path,
    title: 'Lender Hub',
    page: page(() => import('@/features/vendors/pages/LenderHubPage')),
    menu: { label: 'Lender Hub', group: 'Vendors', icon: Landmark },
  },
  {
    path: ROUTES.hubLender.path,
    title: 'Lender',
    page: page(() => import('@/features/vendors/pages/HubLenderPage')),
  },
  {
    path: ROUTES.vendorApi.path,
    title: 'Vendor API',
    page: page(() => import('@/features/vendors/pages/VendorApiPage')),
  },
  {
    path: ROUTES.vendor.path,
    title: 'Vendor',
    page: page(() => import('@/features/vendors/pages/VendorDetailPage')),
  },
  {
    path: ROUTES.events.path,
    title: 'Events',
    page: page(() => import('@/features/events/pages/EventsPage')),
    menu: { label: 'Events', group: 'Admin', icon: Radio },
  },
  {
    path: ROUTES.audit.path,
    title: 'Audit log',
    page: page(() => import('@/features/audit/pages/AuditPage')),
    menu: { label: 'Audit log', group: 'Admin', icon: History },
  },
  {
    path: ROUTES.settings.path,
    title: 'Settings',
    page: page(() => import('@/features/settings/pages/SettingsPage')),
    need: PERMISSION.settingsEdit,
    menu: { label: 'Settings', group: 'Admin', icon: Settings },
  },
  {
    path: ROUTES.users.path,
    title: 'Users',
    page: page(() => import('@/features/users/pages/UsersPage')),
    need: PERMISSION.usersManage,
    menu: { label: 'Users', group: 'Admin', icon: UserCog },
  },
  {
    path: ROUTES.account.path,
    title: 'My account',
    // everyone signed in; opened from the user menu in the top bar
    page: page(() => import('@/features/account/pages/AccountPage')),
  },
]

/** The kit's component gallery, for developers only (npm run dev); never in a build or in the menu. */
export const GALLERY_SCREEN: Screen = {
  path: ROUTES.gallery.path,
  title: 'Kit gallery',
  page: page(() => import('./GalleryPage')),
}
